import { apiClient } from "../../../../lib/axios";

export type QuestionType =
  | "single"
  | "multi"
  | "text"
  | "slider"
  | "dynamic_select"
  | "qrcode";

export type DynamicSource = "department" | "job_role";

export type ValidationRule = "none" | "name" | "email" | "phone_uk";

export interface QuestionOption {
  id: string;
  question_id: string;
  value: string;
  label: string;
  description: string | null;
  display_order: number;
  is_custom_option: boolean;
  custom_placeholder: string | null;
  custom_helper_text: string | null;
}

export interface AdminQuestion {
  id: string;
  client_id: string | null;
  question_type: QuestionType;
  title: string;
  subtitle: string | null;
  helper_text: string | null;
  is_optional: boolean;
  is_pre_registration: boolean;
  is_onboarding: boolean;
  is_always_global: boolean;
  display_order: number;
  min_value: number | null;
  max_value: number | null;
  step_value: number | null;
  unit: string | null;
  default_value: number | null;
  placeholder: string | null;
  is_active: boolean;
  dynamic_source: DynamicSource | null;
  validation_rule: ValidationRule | null;
  created_at: string;
  updated_at: string;
  options: QuestionOption[];
}

export interface AdminQuestionsResponse {
  data: AdminQuestion[];
}

export interface CreateQuestionRequest {
  id?: string;
  title: string;
  subtitle?: string | null;
  helper_text?: string | null;
  is_optional?: boolean;
  question_type: QuestionType;
  is_onboarding?: boolean;
  is_pre_registration?: boolean;
  display_order: number;
  is_active?: boolean;
  dynamic_source?: DynamicSource | null;
  client_id?: string | null;
  min_value?: number | null;
  max_value?: number | null;
  step_value?: number | null;
  unit?: string | null;
  default_value?: number | null;
  placeholder?: string | null;
  validation_rule?: ValidationRule | null;
  options?: Array<{
    value: string;
    label: string;
    description?: string | null;
    display_order?: number;
    is_custom_option?: boolean;
    custom_placeholder?: string | null;
    custom_helper_text?: string | null;
  }>;
}

export interface UpdateQuestionRequest {
  title?: string;
  subtitle?: string | null;
  helper_text?: string | null;
  is_optional?: boolean;
  is_pre_registration?: boolean;
  is_onboarding?: boolean;
  display_order?: number;
  min_value?: number | null;
  max_value?: number | null;
  step_value?: number | null;
  unit?: string | null;
  default_value?: number | null;
  placeholder?: string | null;
  is_active?: boolean;
  dynamic_source?: DynamicSource | null;
  validation_rule?: ValidationRule | null;
}

export interface AddOptionRequest {
  value: string;
  label: string;
  description?: string | null;
  display_order?: number;
  is_custom_option?: boolean;
  custom_placeholder?: string | null;
  custom_helper_text?: string | null;
}

export const questionsApi = {
  listQuestions: async (
    clientId?: string | null,
    type: "onboarding" | "pre_registration" | "all" = "all"
  ): Promise<AdminQuestionsResponse> => {
    const params: Record<string, string> = { type };
    if (clientId) {
      params.client_id = clientId;
    }
    const response = await apiClient.get("/admin/questions", { params });
    return response.data;
  },

  createQuestion: async (
    payload: CreateQuestionRequest
  ): Promise<{ data: AdminQuestion }> => {
    const response = await apiClient.post("/admin/questions", payload);
    return response.data;
  },

  updateQuestion: async (
    questionId: string,
    payload: UpdateQuestionRequest
  ): Promise<{ data: AdminQuestion }> => {
    const response = await apiClient.put(
      `/admin/questions/${encodeURIComponent(questionId)}`,
      payload
    );
    return response.data;
  },

  deleteQuestion: async (questionId: string): Promise<void> => {
    await apiClient.delete(
      `/admin/questions/${encodeURIComponent(questionId)}`
    );
  },

  addOption: async (
    questionId: string,
    payload: AddOptionRequest
  ): Promise<{ data: QuestionOption }> => {
    const response = await apiClient.post(
      `/admin/questions/${encodeURIComponent(questionId)}/options`,
      payload
    );
    return response.data;
  },

  removeOption: async (questionId: string, optionId: string): Promise<void> => {
    await apiClient.delete(
      `/admin/questions/${encodeURIComponent(questionId)}/options/${encodeURIComponent(optionId)}`
    );
  },

  initialiseClientSet: async (
    clientId: string,
    section: "onboarding" | "pre_registration" | "both"
  ): Promise<{ count: number }> => {
    const response = await apiClient.post("/admin/questions/initialize-client-set", {
      client_id: clientId,
      section,
    });
    return response.data;
  },

  copyFromClient: async (
    targetClientId: string,
    sourceClientId: string,
    section: "onboarding" | "pre_registration" | "both"
  ): Promise<{ count: number }> => {
    const response = await apiClient.post("/admin/questions/copy-from-client", {
      target_client_id: targetClientId,
      source_client_id: sourceClientId,
      section,
    });
    return response.data;
  },
};
